<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { stopStore } from "@/_store/stopStore";
import { errorUtils } from "@/utils";

const props = defineProps({
  modalId: { type: String, required: true },
  stopId: {
    type: String,
    default() {
      return null;
    },
  },
  stopIds: {
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["close", "success"]);
const modalStopTimeWindow = ref(null);

const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const stopsStore = stopStore();

const isLoading = ref(false);
const isSaving = ref(false);
const snackbar = ref(null);
const form = ref(buildEmptyForm());
const rules = {
  time_window_1: {
    start_time: {
      required: helpers.withMessage("Hora inicio de Horario 1 es requerida.", required),
    },
    end_time: {
      required: helpers.withMessage("Hora fin de Horario 1 es requerida.", required),
    },
  },
};
const v$ = useVuelidate(rules, form);

function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

const resolvedStopIds = computed(() => {
  if (props.stopIds.length > 0) return props.stopIds;
  return props.stopId ? [props.stopId] : [];
});

const resolvedStopId = computed(() => resolvedStopIds.value[0] || "");

function buildEmptyForm() {
  return {
    time_window_1: {
      start_time: "",
      end_time: "",
    },
    time_window_2: {
      start_time: "",
      end_time: "",
    },
  };
}

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo actualizar el horario de la parada",
    type: "danger",
    color: "white",
  };
}

function loadStopDetail() {
  const stopIdToLoad = resolvedStopId.value;
  if (!stopIdToLoad) {
    showError("Debe seleccionar al menos una parada para cambiar el horario.");
    return;
  }

  isLoading.value = true;
  closeSnackbar();

  stopsStore.getStopById(stopIdToLoad);
}

function saveStopTimeWindow() {
  if (isSaving.value || isLoading.value) return;
  v$.value.$validate();
  if (v$.value.$errors.length > 0) {
    let message = "";
    for (const value of v$.value.$errors) {
      message = `${message}<li>${value.$message}</li>`;
    }
    if (message !== "") {
      snackbar.value = {
        title: "Error",
        message: `<ul>${message}</ul>`,
        type: "danger",
        color: "white",
      };
    }
    return;
  }
  const stopIds = resolvedStopIds.value;
  if (stopIds.length === 0) {
    showError("Debe seleccionar al menos una parada para cambiar el horario.");
    return;
  }
  isSaving.value = true;
  closeSnackbar();

  stopsStore.updateStopTimeWindow(stopIds, {
    time_window_1: {
      start_time: toSafeString(form.value.time_window_1?.start_time),
      end_time: toSafeString(form.value.time_window_1?.end_time),
    },
    time_window_2: {
      start_time: toSafeString(form.value.time_window_2?.start_time),
      end_time: toSafeString(form.value.time_window_2?.end_time),
    },
  });
}

function handleHide() {
  form.value = buildEmptyForm();
  v$.value.$reset();
  emit("close");
}

watch(
  () => stopsStore.stopTimeWindowUpdated,
  (value) => {
    if (!isSaving.value || !value) return;
    isSaving.value = false;
    modalStopTimeWindow.value.closeModal();
    emit("success");
  }
);

watch(
  () => stopsStore.stop,
  (value) => {
    if (!isLoading.value || !value) return;
    form.value.time_window_1 = value.time_window_1;
    form.value.time_window_2 = value.time_window_2;
    v$.value.$reset();
    isLoading.value = false;
  }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!value) return;
    if (!isLoading.value && !isSaving.value) return;
    showError(errorUtils.getErrorMessageHtml(value, "No se pudo actualizar el horario de la parada."));
    isLoading.value = false;
    isSaving.value = false;
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  loadStopDetail();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalStopTimeWindow"
    width="30rem" 
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">Modificar horario</h6>
    </template>

    <template #body>
      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label class="col-form-label required">Horario 1</label>
        </div>
        <div class="col">
          <div class="row g-2">
            <div class="col-6">
              <argon-input
                id="stop-time-window-1-start"
                v-model="form.time_window_1.start_time"
                type="time"
                :error="v$.time_window_1.start_time.$error"
                :is-required="true"
                :disabled="isLoading || isSaving"
              />
            </div>
            <div class="col-6">
              <argon-input
                id="stop-time-window-1-end"
                v-model="form.time_window_1.end_time"
                type="time"
                :error="v$.time_window_1.end_time.$error"
                :is-required="true"
                :disabled="isLoading || isSaving"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label class="col-form-label">Horario 2</label>
        </div>
        <div class="col">
          <div class="row g-2">
            <div class="col-6">
              <argon-input
                id="stop-time-window-2-start"
                v-model="form.time_window_2.start_time"
                type="time"
                :disabled="isLoading || isSaving"
              />
            </div>
            <div class="col-6">
              <argon-input
                id="stop-time-window-2-end"
                v-model="form.time_window_2.end_time"
                type="time"
                :disabled="isLoading || isSaving"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="isSaving ? 'Guardando...' : 'Guardar'"
        :loading="isSaving"
        :disable-button="isLoading || isSaving"
        @click="saveStopTimeWindow"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
